import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { Button, Modal, Typography } from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  PlayCircleOutlined,
  SettingOutlined,
  StopOutlined,
  UndoOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import moment from 'moment';
// Api
import {
  ACTIVATE_STORE_ACCOUNT,
  DEACTIVATE_STORE_ACCOUNT,
} from 'api/store/mutations';
import { REMOVE_INTERVIEWER_CAPABILITY } from 'api/interviewer/mutations';
import {
  // DELETE_USER,
  REJECT_STORE_USER,
  REINSTATE_REJECTED_STORE_USER,
} from 'api/users/mutations';
// Types
import {
  RemoveInterviewerCapability,
  RemoveInterviewerCapabilityVariables,
} from 'api/interviewer/types/RemoveInterviewerCapability';
import { ManageAthleteStoreStatus } from '../ManageStores';
import {
  GetAdminStores,
  GetAdminStoresVariables,
  GetAdminStores_adminStores_entities,
  GetAdminStores_adminStores_entities_signUpSourceStream,
  GetAdminStores_adminStores_entities_sports,
} from 'api/store/types/GetAdminStores';
import { StoreStatus, AdminStoresOrderBy } from 'api/graphql-global-types';
import {
  DeactivateStoreAccount,
  DeactivateStoreAccountVariables,
} from 'api/store/types/DeactivateStoreAccount';
import {
  ActivateStoreAccount,
  ActivateStoreAccountVariables,
} from 'api/store/types/ActivateStoreAccount';
// import { DeleteUser } from 'api/users/types/DeleteUser';
// import { DeleteAddressVariables } from 'api/users/types/DeleteAddress';
import {
  ReinstateRejectedStore,
  ReinstateRejectedStoreVariables,
} from 'api/users/types/ReinstateRejectedStore';
import {
  RejectAwaitingApprovalStore,
  RejectAwaitingApprovalStoreVariables,
} from 'api/users/types/RejectAwaitingApprovalStore';
// UI
import { SortedInfo, TableFilter, Table } from 'ui/Table';
import VideoPreview from 'ui/VideoPreview/VideoPreview';
import { errorNotification, successNotification } from 'ui/Notification';
import RowActionsMenu from 'ui/RowActions/RowActionsMenu';
// Constants
import { commonTimeFormat } from 'constants/global';
import { ATHLETE_DASHBOARD_GEN } from 'constants/routes';
// Hooks
import { useGetSports } from 'hooks';
// Helpers
import { formatCurrencyString } from 'helpers/utils';
// Components
import ConnectForm from './Payout/ConnectForm';
import PayoutForm from './Payout/PayoutForm';
import AvailabilityModal from 'pages/Interviewers/components/AvailabilityModal/AvailabilityModal';
// Columns
import {
  AMAColumn,
  ExperiencesColumn,
  FacebookColumn,
  MillionsFollowersCountColumn,
  SocialFollowersCountColumn,
  InitialAssessmentColumn,
  InstagramColumn,
  LatestAssessmentColumn,
  ManagedByColumn,
  MerchColumn,
  NumberColumn,
  ProductsColumn,
  RevenueColumn,
  StoreStatusColumn,
  StreamColumn,
  TiktokColumn,
  TwitterColumn,
  WebsiteColumn,
  YoutubeColumn,
  OpenDesignRequestColumn,
  CityColumn,
  StateColumn,
  CountryColumn,
} from 'components/common/ManageStoreCommonColumns/ManageStoreCommonColumns';
import InterviewerCapability from 'components/common/InterviewerCapability/InterviewerCapability';

const { confirm } = Modal;
const { Link } = Typography;

export type RejectedStoresTableDataItem = {
  id: number;
  storeId: string;
  name: string;
  email: string;
  phoneNumber: string;
  sports: GetAdminStores_adminStores_entities_sports[];
};

export type UnApprovedStoresTableDataItem = RejectedStoresTableDataItem & {
  hasMerch: string;
  hasAma: string;
  hasStreams: string;
  socialMediaLink: string;
  tiktokLink: string;
  facebookLink: string;
  instagramLink: string;
  website: string;
  twitterLink: string;
  verificationVideo: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  isOnboarded: boolean;
};

export type ApprovedStoresTableDataItem = Omit<
  UnApprovedStoresTableDataItem,
  'phoneNumber' | 'verificationVideo' | 'isOnboarded'
> & {
  followerCount: number | null;
  revenue: number;
  status: StoreStatus | null;
};

type StoresTableDataItem =
  | RejectedStoresTableDataItem
  | ApprovedStoresTableDataItem
  | UnApprovedStoresTableDataItem;

interface Props {
  data: StoresTableDataItem[];
  loading?: boolean;
  refetchStores: (
    variables?: Partial<GetAdminStoresVariables> | undefined
  ) => Promise<ApolloQueryResult<GetAdminStores>>;
  total?: number;
  tabKey?: string;
  sort: SortedInfo<AdminStoresOrderBy>;
  currentPage?: number;
  pageSize?: number;
  setSortInfo: React.Dispatch<
    React.SetStateAction<SortedInfo<AdminStoresOrderBy>>
  >;
  searchFilters: TableFilter[];
  setSearchFilters: React.Dispatch<React.SetStateAction<TableFilter[]>>;
  setPageAndSize?: (page: number, size: number) => void;
  isPayoutsTab?: boolean;
}

const StoresTable: React.FC<Props> = ({
  data,
  loading,
  refetchStores,
  total,
  tabKey,
  sort,
  setSortInfo,
  currentPage,
  pageSize,
  searchFilters,
  setSearchFilters,
  setPageAndSize,
  isPayoutsTab,
}) => {
  const history = useHistory();

  const { data: sportsData } = useGetSports();

  const sports = sportsData?.sports?.map((sport) => {
    return { text: sport.name, value: Number(sport.id) };
  });

  const [isModalVisible, setModalVisible] = useState<boolean>(false);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [selectedRecord, setSelectedRecord] =
    useState<ApprovedStoresTableDataItem | null>(null);
  const [payoutModalData, setPayoutModalData] =
    useState<GetAdminStores_adminStores_entities | null>(null);
  const [connectModalData, setConnectModalData] =
    useState<GetAdminStores_adminStores_entities | null>(null);

  const showPayoutModal = (record: GetAdminStores_adminStores_entities) => {
    setPayoutModalData(record);
  };
  const hidePayoutModal = () => setPayoutModalData(null);

  const showConnectModal = (record: GetAdminStores_adminStores_entities) => {
    setConnectModalData(record);
  };

  const [interviewerCapabilityModalData, setInterviewerCapabilityModalData] =
    useState<GetAdminStores_adminStores_entities | null>(null);

  const [availabilityModalVisible, setAvailabilityModalVisible] =
    useState<boolean>(false);

  const showSetInterviewerCapabilityModal = (
    record: GetAdminStores_adminStores_entities
  ) => {
    setInterviewerCapabilityModalData(record);
  };

  const hideInterviewerCapabilityModal = () =>
    setInterviewerCapabilityModalData(null);

  const hideConnectModal = () => setConnectModalData(null);

  // const [deleteUser] = useMutation<DeleteUser, DeleteAddressVariables>(
  //   DELETE_USER,
  //   {
  //     onCompleted: () => {
  //       refetchStores();
  //     },
  //   }
  // );

  const [removeInterviewerCapability] = useMutation<
    RemoveInterviewerCapability,
    RemoveInterviewerCapabilityVariables
  >(REMOVE_INTERVIEWER_CAPABILITY, {
    onCompleted: () => {
      refetchStores();
    },
  });

  const [rejectStoreUser] = useMutation<
    RejectAwaitingApprovalStore,
    RejectAwaitingApprovalStoreVariables
  >(REJECT_STORE_USER, {
    onCompleted: () => {
      refetchStores();
    },
  });

  const [reinstateRejectedStoreUser] = useMutation<
    ReinstateRejectedStore,
    ReinstateRejectedStoreVariables
  >(REINSTATE_REJECTED_STORE_USER, {
    onCompleted: () => {
      refetchStores();
    },
  });

  const [deactivateStoreAccount] = useMutation<
    DeactivateStoreAccount,
    DeactivateStoreAccountVariables
  >(DEACTIVATE_STORE_ACCOUNT);

  const [activateStoreAccount] = useMutation<
    ActivateStoreAccount,
    ActivateStoreAccountVariables
  >(ACTIVATE_STORE_ACCOUNT);
  // COMMENTED: requested to remove delete function for now, possible re-activation in the future
  // const handleDeleteUser = async (id: string) => {
  //   confirm({
  //     title: `Are you sure you want to delete this user?`,
  //     icon: <ExclamationCircleOutlined />,
  //     okText: 'Yes',
  //     okType: 'danger',
  //     cancelText: 'No',
  //     onOk: async () => {
  //       try {
  //         await deleteUser({
  //           variables: { id },
  //         });
  //         successNotification('User is deleted');
  //       } catch (e: any) {
  //         errorNotification();
  //         console.log('deleteUser error:', { ...e });
  //       }
  //     },
  //   });
  // };

  const handleRejectStore = async (storeId: string) => {
    try {
      await rejectStoreUser({
        variables: { input: { storeId } },
      });
      refetchStores();
      successNotification('Store has been rejected');
    } catch (err) {
      errorNotification((err as Error)?.message);
      console.log('handleRejectStore error:', { ...(err as Error) });
    }
  };

  const handleReinstateStore = async (storeId: string) => {
    try {
      await reinstateRejectedStoreUser({
        variables: { input: { storeId } },
      });
      refetchStores();
      successNotification('Store has been reinstated');
    } catch (err) {
      errorNotification((err as Error)?.message);
      console.log('handleReinstateStore error:', { ...(err as Error) });
    }
  };

  const handleDeactivateAccount = async (storeId: string) => {
    try {
      await deactivateStoreAccount({
        variables: { input: { storeId } },
      });
      refetchStores();
      successNotification('Store has been deactivated');
    } catch (err) {
      errorNotification((err as Error)?.message);
      console.log('handleDeactivateAccount error:', { ...(err as Error) });
    }
  };

  const handleActivateAccount = async (storeId: string) => {
    try {
      await activateStoreAccount({
        variables: { input: { storeId } },
      });
      refetchStores();
      successNotification('Store has been activated');
    } catch (err) {
      errorNotification();
    }
  };

  const handleRemoveInterviewerCapability = async (storeId: string) => {
    try {
      await removeInterviewerCapability({
        variables: { input: { storeId } },
      });
      refetchStores();
      successNotification(
        'Interviewer has been removed from interviewer table'
      );
    } catch (err) {
      errorNotification();
    }
  };

  const handleDeactivateStoreConfirmation = (id: string, name: string) => {
    confirm({
      title: `Are you sure to deactivate the store "${name}"?`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        handleDeactivateAccount(id);
      },
    });
  };

  const handleRemoveInterviewerCapabilityConfirmation = (
    id: string,
    name: string
  ) => {
    confirm({
      title: `Are you sure you want to remove interviewer from interviewer table for the "${name}"?`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        handleRemoveInterviewerCapability(id);
      },
    });
  };

  const handleActivateStoreConfirmation = (id: string, name: string) => {
    confirm({
      title: `Are you sure to activate the store "${name}"?`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        handleActivateAccount(id);
      },
    });
  };

  const handleManageStore = async (storeId: string) => {
    await history.push(`${ATHLETE_DASHBOARD_GEN}/${storeId}`);
  };

  const handleShowVideo = (videoUrl: string) => {
    setModalVisible(true);
    setVideoPreview(videoUrl);
  };

  const handleAvailabilityModalToggle = () => {
    setAvailabilityModalVisible((prev) => !prev);
  };

  const handleHideModal = () => {
    setModalVisible(false);
  };

  const handleAcceptUnapproved = (record: UnApprovedStoresTableDataItem) => {
    if (record.isOnboarded) {
      handleActivateAccount(record.storeId);
    } else {
      errorNotification('Store is not onboarded.');
    }
  };

  const NameColumn = {
    title: 'Name',
    dataIndex: 'name',
    key: AdminStoresOrderBy.firstName,
    fixed: 'left' as const,
    width: 150,
    sorterType: 'text',
    withSearch: true,
  };

  const AthleteColumn = {
    title: "Athlete's email",
    dataIndex: 'email',
    key: AdminStoresOrderBy.email,
    fixed: 'left' as const,
    width: 150,
    sorterType: 'text',
    withSearch: true,
  };

  const PhoneColumn = {
    title: 'Phone number',
    dataIndex: 'phoneNumber',
    key: AdminStoresOrderBy.phoneNumber,
    align: 'center' as const,
    width: 180,
    withSearch: true,
    sorterType: 'text',
  };

  const PPVEventName = {
    title: 'PPV event name',
    dataIndex: 'signUpSourceStream',
    align: 'center' as const,
    width: 130,
    render: (
      signUpSourceStream: GetAdminStores_adminStores_entities_signUpSourceStream
    ) => {
      if (signUpSourceStream) {
        return signUpSourceStream;
      }
    },
  };

  const SignUpDateColumn = {
    title: 'Sign up date',
    dataIndex: 'createdAt',
    key: AdminStoresOrderBy.createdAt,
    align: 'center' as const,
    width: 180,
    sorterType: 'date',
    render: (createdAt: string) => {
      return moment(createdAt).format(commonTimeFormat);
    },
    withDateRangeFilter: true,
  };

  const ActivatedAtDateColumn = {
    title: 'Activated at date',
    dataIndex: 'activatedAt',
    key: AdminStoresOrderBy.activatedAt,
    align: 'center' as const,
    width: 180,
    sorterType: 'date',
    render: (activatedAt: string) => {
      if (activatedAt) {
        return moment(activatedAt).format(commonTimeFormat);
      }
    },
    withDateRangeFilter: true,
  };

  const AmaVideoPreviewColumn = {
    title: 'AMA Video',
    dataIndex: 'verificationVideo',
    align: 'center' as const,
    width: 130,
    key: AdminStoresOrderBy.amaVideo,
    render: function VerificationVideo(video: string | null) {
      if (video) {
        return (
          <button
            className="storesTablePlayButton"
            onClick={() => handleShowVideo(video)}
          >
            <PlayCircleOutlined style={{ fontSize: '24px' }} />
          </button>
        );
      }
    },
  };

  const SportColumn = {
    key: 'sportId',
    filters: sports,
    filterMultiple: true,
    withRadioFilters: true,
    title: 'Kinds of sport',
    dataIndex: 'sports',
    align: 'center' as const,
    width: 150,
    render: function Sports(sports: StoresTableDataItem['sports']) {
      const sportList = sports?.map((item) => `${item.name}`).join(', ') || '';
      return sportList;
    },
  };

  const SocialMediaColumn = {
    title: 'Social media link',
    dataIndex: 'socialMediaLink',
    align: 'left' as const,
    width: 220,
    render: function SocialMediaLink(socialMediaLink: string) {
      return (
        <Link href={socialMediaLink} target="_blank">
          {socialMediaLink}
        </Link>
      );
    },
  };

  const rejectedStoresColumns = [
    NumberColumn,
    NameColumn,
    AthleteColumn,
    SignUpDateColumn,
    PhoneColumn,
    AmaVideoPreviewColumn,
    SportColumn,
    ManagedByColumn,
    SocialFollowersCountColumn,
    FacebookColumn,
    YoutubeColumn,
    TiktokColumn,
    InstagramColumn,
    TwitterColumn,
    InitialAssessmentColumn,
    LatestAssessmentColumn,
    CountryColumn,
    StateColumn,
    CityColumn,
    {
      title: 'Actions',
      dataIndex: 'actions',
      fixed: 'right' as const,
      align: 'center' as const,
      width: 110,
      // TODO fix type of record
      render: function Actions(text: any, record: any) {
        return (
          <Button
            type="primary"
            className="basic-table__button_success"
            onClick={() => handleReinstateStore(record.storeId)}
          >
            Reinstate
          </Button>
        );
      },
    },
  ];

  const unApprovedStoresColumns = [
    NumberColumn,
    NameColumn,
    AthleteColumn,
    PhoneColumn,
    SignUpDateColumn,
    PPVEventName,
    MerchColumn,
    AMAColumn,
    StreamColumn,
    ProductsColumn,
    ExperiencesColumn,
    AmaVideoPreviewColumn,
    SportColumn,
    SocialMediaColumn,
    SocialFollowersCountColumn,
    FacebookColumn,
    YoutubeColumn,
    TiktokColumn,
    InstagramColumn,
    TwitterColumn,
    WebsiteColumn,
    ManagedByColumn,
    InitialAssessmentColumn,
    LatestAssessmentColumn,
    CountryColumn,
    StateColumn,
    CityColumn,
    {
      title: 'Actions',
      dataIndex: 'actions',
      fixed: 'right' as const,
      align: 'center' as const,
      width: 80,
      render: function Actions(text: any, record: any) {
        return (
          <RowActionsMenu
            label={`Actions for ${record.name}`}
            actions={[
              {
                key: 'manage',
                label: 'Manage store',
                icon: <SettingOutlined />,
                onClick: () => handleManageStore(record.storeId),
              },
              {
                key: 'accept',
                label: 'Accept sign up',
                icon: <CheckCircleOutlined />,
                positive: true,
                startsGroup: true,
                onClick: () => handleAcceptUnapproved(record),
              },
              {
                key: 'reject',
                label: 'Reject sign up',
                icon: <CloseCircleOutlined />,
                danger: true,
                onClick: () => handleRejectStore(record.storeId),
              },
            ]}
          />
        );
      },
    },
  ];

  const approvedStoresColumns = [
    NumberColumn,
    NameColumn,
    AthleteColumn,
    PhoneColumn,
    SignUpDateColumn,
    PPVEventName,
    ActivatedAtDateColumn,
    RevenueColumn,
    StoreStatusColumn,
    MerchColumn,
    AMAColumn,
    StreamColumn,
    ProductsColumn,
    ExperiencesColumn,
    AmaVideoPreviewColumn,
    SportColumn,
    SocialMediaColumn,
    MillionsFollowersCountColumn,
    SocialFollowersCountColumn,
    FacebookColumn,
    YoutubeColumn,
    TiktokColumn,
    InstagramColumn,
    TwitterColumn,
    WebsiteColumn,
    OpenDesignRequestColumn,
    ManagedByColumn,
    InitialAssessmentColumn,
    LatestAssessmentColumn,
    CountryColumn,
    StateColumn,
    CityColumn,
    {
      title: 'Actions',
      dataIndex: 'actions',
      fixed: 'right' as const,
      align: 'center' as const,
      width: 80,
      render: function Actions(text: any, record: any) {
        const isActive = record?.status === StoreStatus.Active;

        return (
          <RowActionsMenu
            label={`Actions for ${record.name}`}
            actions={[
              {
                key: 'manage',
                label: 'Manage store',
                icon: <SettingOutlined />,
                onClick: () => handleManageStore(record.storeId),
              },
              {
                key: 'connect',
                label: 'Connect',
                icon: <LinkOutlined />,
                hidden: !isActive,
                onClick: () => showConnectModal(record),
              },
              {
                key: 'payout',
                label: 'Payout',
                icon: <DollarOutlined />,
                hidden: !isActive,
                disabled: !record.hasConnectedAccount,
                onClick: () => showPayoutModal(record),
              },
              {
                key: 'availability',
                label: 'Availability',
                icon: <CalendarOutlined />,
                hidden: !isActive,
                onClick: () => {
                  setSelectedRecord(record);
                  handleAvailabilityModalToggle();
                },
              },
              {
                key: 'add-interviewer',
                label: 'Add to interviewer table',
                icon: <UserAddOutlined />,
                startsGroup: true,
                hidden: !isActive || !!record.interviewerDetails,
                onClick: () => showSetInterviewerCapabilityModal(record),
              },
              {
                key: 'remove-interviewer',
                label: 'Remove from interviewer table',
                icon: <UserDeleteOutlined />,
                startsGroup: true,
                hidden: !isActive || !record.interviewerDetails,
                onClick: () =>
                  handleRemoveInterviewerCapabilityConfirmation(
                    record.storeId,
                    record.name
                  ),
              },
              {
                key: 'activate',
                label: 'Activate store',
                icon: <UndoOutlined />,
                positive: true,
                startsGroup: true,
                hidden: isActive,
                onClick: () =>
                  handleActivateStoreConfirmation(record.storeId, record.name),
              },
              {
                key: 'deactivate',
                label: 'Deactivate store',
                icon: <StopOutlined />,
                danger: true,
                startsGroup: true,
                hidden: !isActive,
                onClick: () =>
                  handleDeactivateStoreConfirmation(
                    record.storeId,
                    record.name
                  ),
              },
            ]}
          />
        );
      },
    },
  ];

  const payoutsData = [
    {
      title: 'id',
      dataIndex: 'id',
      align: 'center' as const,
      fixed: 'left' as const,
      width: 90,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: AdminStoresOrderBy.firstName,
      fixed: 'left' as const,
      width: 150,
      sorterType: 'text',
      withSearch: true,
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: AdminStoresOrderBy.revenue,
      align: 'center' as const,
      width: 180,
      sorterType: 'number',
      render: (revenue: number) => formatCurrencyString(revenue),
    },

    {
      title: 'Payout period',
      dataIndex: 'payoutPeriod',
      key: 'payoutPeriod',
      align: 'center' as const,
      width: 180,
    },
    {
      title: 'Profit',
      dataIndex: 'profit',
      key: 'profit',
      align: 'center' as const,
      width: 180,
      render: (profit: number) => formatCurrencyString(profit),
    },
    {
      title: 'Outstanding balance',
      dataIndex: 'outstandingBalance',
      key: 'outstandingBalance',
      align: 'center' as const,
      width: 180,
      render: (outstandingBalance: number) =>
        formatCurrencyString(outstandingBalance),
    },
  ];

  return (
    <>
      <Table<StoresTableDataItem, AdminStoresOrderBy>
        columns={
          isPayoutsTab
            ? payoutsData
            : tabKey === ManageAthleteStoreStatus.Pending
            ? unApprovedStoresColumns
            : tabKey === ManageAthleteStoreStatus.Rejected
            ? rejectedStoresColumns
            : approvedStoresColumns
        }
        data={data}
        scroll={{ x: 1280 }}
        loading={loading}
        total={total}
        defaultPageSize={pageSize}
        defaultCurrent={currentPage}
        searchFilters={searchFilters}
        setSearchFilters={setSearchFilters}
        setPageAndSize={setPageAndSize}
        sortInfo={sort}
        setSortInfo={setSortInfo}
      />
      <Modal
        title="Video Preview"
        visible={isModalVisible}
        footer={null}
        onCancel={handleHideModal}
      >
        <div className="videoWrapper">
          <VideoPreview video={videoPreview} show={isModalVisible} />
        </div>
      </Modal>

      <Modal
        title="Connect"
        visible={connectModalData ? true : false}
        footer={null}
        onCancel={hideConnectModal}
      >
        {connectModalData && <ConnectForm record={connectModalData} />}
      </Modal>

      <Modal
        title="Interviewer Capability"
        visible={interviewerCapabilityModalData ? true : false}
        footer={null}
        onCancel={hideInterviewerCapabilityModal}
      >
        {interviewerCapabilityModalData && (
          <InterviewerCapability
            record={interviewerCapabilityModalData}
            refetchStores={refetchStores}
            hideInterviewerCapabilityModal={hideInterviewerCapabilityModal}
          />
        )}
      </Modal>

      <Modal
        title="Payout"
        visible={payoutModalData ? true : false}
        footer={null}
        onCancel={hidePayoutModal}
      >
        {payoutModalData && (
          <PayoutForm
            record={payoutModalData}
            hidePayoutModal={hidePayoutModal}
          />
        )}
      </Modal>

      {selectedRecord && (
        <AvailabilityModal
          isVisible={availabilityModalVisible}
          onClose={handleAvailabilityModalToggle}
          storeId={selectedRecord.storeId}
        />
      )}
    </>
  );
};

export default StoresTable;
